import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';

import { makeStyles } from '@mui/styles';
import Grid from '@mui/material/Grid';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import TextField from '@mui/material/TextField';
import Radio from '@mui/material/Radio';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import RadioGroup from '@mui/material/RadioGroup';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Checkbox from '@mui/material/Checkbox';

import BackIcon from '@mui/icons-material/ArrowBack';
import ResetIcon from '@mui/icons-material/Refresh';
import SaveIcon from '@mui/icons-material/Save';

import _get from 'lodash/get';
import _has from 'lodash/has';
import _cloneDeep from 'lodash/cloneDeep';

import OSAnomolyRulesService from '../../../../data/os-anomaly-rules-service';
import Auth from '../../../../data/auth-service';

import { RuleTitle } from '../../../../libraries/helpers';

const useStyles = makeStyles(theme => ({
    root: {},
    buttonRight: {
        float: 'right',
        marginLeft: theme.spacing(2),
    },
    header: {
        padding: theme.spacing(2),
    },
}));

const OpenSearchAnomaliesEditor = (props) => {
    const classes = useStyles();
    const params = useParams();
    const navigate = useNavigate();

    const perms = Auth.permissions.get('OpenSearchAnomalies');

    const [detector, setDetector] = useState();
    const [filters, setFilters] = useState({});
    const [operator, setOperator] = useState('and');
    const [enabled, setEnabled] = useState(false);

    const [ogFilters, setOGFilters] = useState({});
    const [ogDetector, setOGDetector] = useState();
    const [modified, setModified] = useState(false);

    const [preview, setPreview] = useState('');

    const getDetector = (id) => {
        OSAnomolyRulesService.getDetector(id)
            .then(res => {
                setDetector(res || {});
                if (_has(res, 'anomaly_detector_job')) {
                    setEnabled(res.anomaly_detector_job.enabled);
                } else {
                    setEnabled(false);
                }
                setOGDetector(_cloneDeep(res));
            }).catch(err => {
                toast.error('Unable to get detector');
                console.error(err);
            });
    };

    const getFilters = (name, id) => {
        OSAnomolyRulesService.listRuleFilters(name, id).then(res => {
            // console.log(res);
            setFilters(res?.filters);
            setOperator(res?.operator || 'and');
            setOGFilters(_cloneDeep(res));
            setModified(false);
        }).catch(err => {
            toast.error('Unable to get rule filters');
            console.error(err);
        });
    };

    const handleEnabled = (e) => {
        setEnabled(e.target.checked);
    };

    const handleFilterEnabled = (e, filter) => {
        const f = _cloneDeep(filters);
        if (_has(f, filter)) {
            f[filter] = { ...f[filter], enabled: e.target.checked };
        }

        setFilters(f);
    };

    const onFilterOperatorChange = (e, filter) => {
        setFilters(current => ({
            ...current,
            [filter]: {
                ..._get(current, filter, {}),
                operator: e.target.value,
            },
        }));
    };

    const onFilterValueChange = (e, filter) => {
        setFilters(current => ({
            ...current,
            [filter]: {
                ..._get(current, filter, {}),
                value: parseFloat(e.target.value),
            },
        }));
    };

    const onUpdate = () => {
        if (!detector?._id) return;
        OSAnomolyRulesService.updateRuleFilters(params.detector || '', detector._id, { filters, operator })
            .then(() => {
                if (enabled !== detector?.enabled) {
                    OSAnomolyRulesService.enableAnomalyRule(detector._id, enabled).then(() => {
                        toast.success('Updated detector');
                        navigate('/system/anomaly_detection/opensearch');
                    }).catch(err => {
                        toast.error(`Failed to ${enabled ? 'enable' : 'disable'} detector`);
                        console.error(err);
                    });
                } else {
                    toast.success('Updated detector');
                    navigate('/system/anomaly_detection/opensearch');
                }
            }).catch(err => {
                toast.error('Failed to update detector');
                console.error(err);
            });
    };

    const onReset = () => {
        setFilters(ogFilters?.filters || {});
        setOperator(ogFilters?.operator || 'and');
        setEnabled(_get(ogDetector, 'anomaly_detector_job.enabled', false));
    };

    const onBack = () => {
        navigate('/system/anomaly_detection/opensearch');
    };

    useEffect(() => {
        const routeQuery = new URLSearchParams(location.search);
        if (!routeQuery.has('id')) return;
        getDetector(routeQuery.get('id'));
        getFilters(params.detector || '', routeQuery.get('id'));
    }, [params.detector]);

    useEffect(() => {
        if (detector && _has(detector, '_id')) {
            if(ogFilters) {
                let checkFilters = false;
                let checkOperator = false;
                let checkEnabled = false;

                if(_has(ogFilters, 'filters')) {
                    checkFilters = JSON.stringify(filters) !== JSON.stringify(ogFilters?.filters);
                }
                checkOperator = operator !== ogFilters?.operator;
                checkEnabled = _get(ogDetector, 'anomaly_detector_job.enabled', false) !== enabled;

                setModified(checkFilters || checkOperator || checkEnabled);
            }

        }
    }, [filters, operator, detector, enabled]);

    useEffect(() => {
        if (!detector) return;
        const grade = _get(filters, 'grade', {});
        const confidence = _get(filters, 'confidence', {});

        const gPreview = grade.enabled ? `Anomaly Grade ${grade.operator} ${grade.value}` : '';
        const cPreview = confidence.enabled ? `Confidence ${confidence.operator} ${confidence.value}` : '';

        // filter(Boolean) is a cool trick. thank you co-pilot
        const prev = [gPreview, cPreview]
            .filter(Boolean)
            .join(` ${operator.toUpperCase()} `);
        setPreview(prev || 'No filters applied');
    }, [filters, operator, detector]);

    return (
        <Box>
            <Grid container spacing={2}>
                <Grid container item xs={12} style={{ display: 'flex' }}>
                    <Grid container item xs={12} className={classes.header}>
                        <Grid item xs={6}>
                            <h3>{RuleTitle(_get(detector, 'anomaly_detector.name', ''))}</h3>
                        </Grid>
                        <Grid item xs={6} className={classes.buttonGroup}>
                            <Button
                                variant="contained"
                                color="secondary"
                                aria-label="update"
                                className={classes.buttonRight}
                                disabled={!perms?.write || !modified}
                                onClick={onUpdate}
                            ><SaveIcon /> Update </Button>
                            <Button
                                variant="contained"
                                color="primary"
                                aria-label="reset"
                                className={classes.buttonRight}
                                onClick={onReset}
                                disabled={!perms?.write || !modified}
                            ><ResetIcon/> Reset </Button>
                            <Button
                                variant="contained"
                                color="primary"
                                aria-label="back"
                                className={classes.buttonRight}
                                onClick={onBack}
                            ><BackIcon/> Back </Button>
                        </Grid>
                    </Grid>
                </Grid>
            </Grid>
            <Grid>
                <Card>
                    <Box p={2} > Rule Details </Box>
                    <Box p={2} >
                        <Grid container spacing={2}>
                            <Grid item xs={12}>
                                <TextField
                                    disabled
                                    fullWidth
                                    id="detector_id"
                                    label="Detector ID"
                                    value={_get(detector, '_id', '')}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    disabled
                                    fullWidth
                                    id="name"
                                    label="Name"
                                    value={RuleTitle(_get(detector, 'anomaly_detector.name', ''))}
                                    onChange={(e) => setDetector({ ...detector, name: e.target.value })}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    disabled
                                    fullWidth
                                    multiline
                                    id="description"
                                    label="Description"
                                    value={_get(detector, 'anomaly_detector.description', '')}
                                    onChange={(e) => setDetector({ ...detector, description: e.target.value })}
                                />
                            </Grid>
                        </Grid>
                    </Box>
                    <Box p={2}>
                        <FormControl>
                            <FormControlLabel
                                control={<Checkbox
                                    checked={enabled || false}
                                    onChange={handleEnabled}
                                />}
                                label="Activated"
                                labelPlacement="end"
                            />
                        </FormControl>
                    </Box>
                    <Box p={2} > Filter Parameters </Box>
                    <Card variant="outlined" style={{ margin: 10, padding: 20 }}>
                        Per rule filter anomalies based on confidence score and anomaly grade,
                        where higher vlues indicate true anomalies.

                        {/*anomaly grade filter*/}
                        <Grid container spacing={2}>
                            <Grid>
                                <Box p={2}>
                                    <FormControlLabel
                                        style={{ minWidth: 170 }}
                                        control={<Checkbox
                                            checked={filters?.grade?.enabled || false}
                                            onChange={e => handleFilterEnabled(e, 'grade')}
                                        />}
                                        label="Anomaly Grade"
                                        labelPlacement="end"
                                    />

                                    <FormControl sx={{ m: 1, minWidth: 250 }} size="small">
                                        <InputLabel>Select Operator</InputLabel>
                                        <Select
                                            variant={'outlined'}
                                            value={filters?.grade?.operator || '='}
                                            label="Operator"
                                            onChange={e => onFilterOperatorChange(e, 'grade')}>
                                            {OSAnomolyRulesService.filterOperators.map(fo =>
                                                <MenuItem key={fo.value} value={fo.value}>{fo.label}</MenuItem>
                                            )}
                                        </Select>
                                    </FormControl>

                                    <TextField
                                        sx={{ m: 1, minWidth: 150 }}
                                        size="small"
                                        label="Value"
                                        type="number"
                                        inputProps= {{
                                            min: 0,
                                            max: 1,
                                            step: 0.1,
                                            value: filters?.grade?.value || 0,
                                        }}
                                        value={filters?.grade?.value || 0}
                                        onChange={e => onFilterValueChange(e, 'grade')}
                                    />
                                </Box>
                            </Grid>
                        </Grid>

                        {/*confidence level filter*/}
                        <Grid container spacing={2}>
                            <Grid>
                                <Box p={2}>
                                    <FormControlLabel
                                        control={<Checkbox
                                            checked={filters?.confidence?.enabled || false}
                                            onChange={e => handleFilterEnabled(e, 'confidence')}
                                        />}
                                        label="Confidence Level"
                                        labelPlacement="end"
                                    />

                                    <FormControl sx={{ m: 1, minWidth: 250 }} size="small">
                                        <InputLabel>Select Operator</InputLabel>
                                        <Select
                                            variant={'outlined'}
                                            value={filters?.confidence?.operator || '='}
                                            label="Operator"
                                            onChange={e => onFilterOperatorChange(e, 'confidence')}>
                                            {OSAnomolyRulesService.filterOperators.map(fo =>
                                                <MenuItem key={fo.value} value={fo.value}>{fo.label}</MenuItem>
                                            )}
                                        </Select>
                                    </FormControl>

                                    <TextField
                                        sx={{ m: 1, minWidth: 150 }}
                                        size="small"
                                        label="Value"
                                        type="number"
                                        inputProps= {{
                                            min: 0,
                                            max: 1,
                                            step: 0.1,
                                            value: filters?.confidence?.value || 0,
                                        }}
                                        value={filters?.confidence?.value || 0}
                                        onChange={e => onFilterValueChange(e, 'confidence')}
                                    />
                                </Box>
                            </Grid>
                        </Grid>

                        {/*operator*/}
                        <Box p={2}>
                            <FormControl>
                                <RadioGroup
                                    row
                                    value={operator || 'and'}
                                    onChange={(e) => setOperator(e.target.value)}
                                >
                                    <FormControlLabel value="and" control={<Radio />} label="AND" />
                                    <FormControlLabel value="or" control={<Radio />} label="OR" />
                                </RadioGroup>
                            </FormControl>
                        </Box>

                        {/*preview*/}
                        <Card style={{ padding: 20 }}>
                            <h4>Preview</h4>
                            <p>
                                {preview}
                            </p>
                        </Card>
                    </Card>
                </Card>
            </Grid>
        </Box>
    );
};

export default OpenSearchAnomaliesEditor;
